import { Inject, Injectable } from "@angular/core";
import { ColumnConfigType, OptionalColumn, SimpleTableColumnConfig } from "@nms-angular-toolkit/nms-simple-table";
import { ANGULARJS_TRANSLATE, LABEL_COLOR_SERVICE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { PRIMARY_HOVERED_COLOR, TRANSPARENT_COLOR, WHITE_COLOR } from "@nms-ng2/app/shared/constants/colors";
import {
    AlarmDiagnosticData,
    AmplifierDiagnosticData,
    CIRCUIT_STATUS,
    CircuitStatus,
    DwdmPortDiagnosticData,
    LINKS_STATUS_TRANSLATION_MAP,
    LinkStatus
} from "../../dwdm-circuit.model";
import { LINK_SITUATION, STATUS } from "@nms-ng2/app/modules/topology/links/links.model";
import { ModalFactory } from "@nms-ng2/app/shared/services/modal/modal.factory";
import { AlarmDetailsComponent } from "../alarm-details/alarm-details.component";
import { CircuitPortIdentifierData } from "../../wizard/steps/6-osc-management/osc-management-step.model";
import {
    MILI_AMP,
    DECIBEL_MILIWAT,
    CELSIUS,
    VOLT,
    DECIBEL,
    KILOMETER,
    DECIBEL_BY_KILOMETER
} from "@nms-ng2/app/shared/constants/units";

/**
 * Serviço responsável por retornar os dados das colunas relacionadas as abas
 * das tabelas na modal de diagnóstico do circuito DWDM.
 */
@Injectable({
    providedIn: "root"
})
export class DiagnosticTableColumnsService {
    identifier: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.identifier");
    equipment: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.device");
    transceiverId: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.transceiver.id");
    temperature: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.temperature");
    voltage: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.voltage");
    current: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.current");
    rxPower: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.rxPower");
    txPower: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.txPower");
    osnr: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.osnr");
    edfa: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.edfa");
    inputPower: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.input.power");
    outputPower: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.output.power");
    signalPower: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.signal.power");
    gain: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.gain");
    ldCurrent: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.ld.current");
    ldEolCurrent: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.ld.eol.current");
    osc: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.osc.id");
    linkStatus: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.status");
    name: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.name");
    aEnd: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.aEnd");
    zEnd: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.zEnd");
    distance: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.distance");
    attenuation: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.attenuation");
    sourceType: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.source.type");
    sourceValue: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.source.value");
    info: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.info");
    time: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.time");
    unstable: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.unstable");
    severity: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.severity");
    alarms: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.alarms");
    prefecBer: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.prefec.ber");
    ferc: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.ferc");
    qvalue: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.qvalue");
    qfactor: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.qfactor");
    tecCurrent: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.tec.current");
    tecVoltage: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.tec.voltage");
    supplyVoltage: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.supply.voltage");
    ldTemperature: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.ld.temperature");
    caseTemperature: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.case.temperature");
    amplifier: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.amplifier");
    totalAttenuation: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.total.attenuation");
    situation: string = this.translate.instant("dwdm.circuit.modal.diagnostic.table.situation");

    textToShowWhenEmpty = "N/A";

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            @Inject(LABEL_COLOR_SERVICE) private readonly labelColorService: any,
            @Inject(ModalFactory) private readonly modalFactory: ModalFactory
    ) {}

    buildTransceiversHundredColumns(): SimpleTableColumnConfig[] {
        return [
            {
                field: "equipment",
                label: this.equipment,
                textToShowWhenEmpty: this.textToShowWhenEmpty,
                getCustomText: (row) => row?.deviceName
            },
            {
                field: "transceiver",
                label: this.transceiverId,
                optionalColumn: this.getTextTipIconOptionalColumn(),
                getCustomText: (row) => this.getPortIdentifier(row)
            },
            {
                field: "temperature",
                label: this.temperature,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.transceiverDiagnosticData?.temperature, CELSIUS)
            },
            {
                field: "voltage",
                label: this.voltage,
                getCustomText: (row) => this.showTextOrPlaceHolder(row?.transceiverDiagnosticData?.voltage, VOLT)
            },
            {
                field: "current",
                label: this.current,
                getCustomText: (row) => this.showTextOrPlaceHolder(row?.transceiverDiagnosticData?.current, MILI_AMP)
            },
            {
                field: "txPower",
                label: this.txPower,
                getCustomText: (row) => this.showTextOrPlaceHolder(row?.transceiverDiagnosticData?.txPower, DECIBEL_MILIWAT)
            },
            {
                field: "rxPower",
                label: this.rxPower,
                getCustomText: (row) => this.showTextOrPlaceHolder(row?.transceiverDiagnosticData?.rxPower, DECIBEL_MILIWAT)
            },
            {
                field: "linkStatus",
                label: this.linkStatus,
                getCustomText: (row) => this.getLinkStatusText(row.linkStatus)
            },
            {
                field: "alarms",
                label: this.alarms,
                optionalColumn: this.getAlarmOptionalColumn("alarmsDiagnosticData")
            }
        ];
    }

    buildTransceiversHundredColumnsSortable(): Record<string, boolean> {
        const columns = [
            "equipment",
            "transceiver",
            "temperature",
            "voltage",
            "current",
            "txPower",
            "rxPower",
            "linkStatus",
            "alarms"
        ];

        return this.buildAllColumnsSortable(columns);
    }

    buildTransceiversFourHundredColumns(): SimpleTableColumnConfig[] {
        return [
            {
                field: "equipment",
                label: this.equipment,
                width: "190px",
                getCustomText: (row) => this.showTextOrPlaceHolder(row.deviceName)
            },
            {
                field: "transceiver",
                label: this.transceiverId,
                width: "200px",
                optionalColumn: this.getTextTipIconOptionalColumn(),
                getCustomText: (row) => this.getPortIdentifier(row)
            },
            {
                field: "temperature",
                label: this.temperature,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.temperature, CELSIUS)
            },
            {
                field: "voltage",
                label: this.voltage,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.voltage, VOLT)
            },
            {
                field: "current",
                label: this.current,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.current, MILI_AMP)
            },
            {
                field: "txPower",
                label: this.txPower,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.txPower, DECIBEL_MILIWAT)
            },
            {
                field: "rxPower",
                label: this.rxPower,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.rxPower, DECIBEL_MILIWAT)
            },
            {
                field: "osnr",
                label: this.osnr,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.osnr, DECIBEL)
            },
            {
                field: "prefecBer",
                label: this.prefecBer,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.prefecBer)
            },
            {
                field: "ferc",
                label: this.ferc,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.ferc)
            },
            {
                field: "qvalue",
                label: this.qvalue,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.qValue)
            },
            {
                field: "qfactor",
                label: this.qfactor,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.dwdmTransceiverDiagnosticData?.qFactor, DECIBEL)
            },
            {
                field: "alarms",
                label: this.alarms,
                optionalColumn: this.getAlarmOptionalColumn("alarmsDiagnosticData")
            }
        ];
    }

    buildTransceiversFourHundredColumnsSortable(): Record<string, boolean> {
        const columns = [
            "equipment",
            "transceiver",
            "temperature",
            "voltage",
            "current",
            "txPower",
            "rxPower",
            "osnr",
            "prefecBer",
            "ferc",
            "qvalue",
            "qfactor",
            "alarms"
        ];

        return this.buildAllColumnsSortable(columns);
    }

    buildAmplifiersColumns(): SimpleTableColumnConfig[] {
        return [
            {
                field: "equipment",
                label: this.equipment,
                width: "190px",
                getCustomText: (row) => this.showTextOrPlaceHolder(row.deviceName)
            },
            {
                field: "edfa",
                label: this.edfa,
                width: "190px",
                optionalColumn: this.getTextTipIconAmplifiersOptionalColumn(),
                getCustomText: (row) => this.getPortFormatedIdentifier(row.portIdentifier)
            },
            { field: "inputPower", label: this.inputPower, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "outputPower", label: this.outputPower, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "signalPower", label: this.signalPower, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "gain", label: this.gain, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "ldCurrent", label: this.ldCurrent, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "ldEolCurrent", label: this.ldEolCurrent, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "tecCurrent", label: this.tecCurrent, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "tecVoltage", label: this.tecVoltage, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "supplyVoltage", label: this.supplyVoltage, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "ldTemperature", label: this.ldTemperature, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "caseTemperature", label: this.caseTemperature, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "amplifier", label: this.amplifier, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "alarms", label: this.alarms, width: "200px", optionalColumn: this.getAlarmOptionalColumn("alarms") }
        ];
    }

    buildAmplifiersColumnsSortable(): Record<string, boolean> {
        const columns = [
            "equipment",
            "edfa",
            "inputPower",
            "outputPower",
            "signalPower",
            "gain",
            "rxPower",
            "ldCurrent",
            "ldEolCurrent",
            "tecCurrent",
            "tecVoltage",
            "supplyVoltage",
            "ldTemperature",
            "caseTemperature",
            "amplifier",
            "alarms"
        ];

        return this.buildAllColumnsSortable(columns);
    }

    buildOscColumns(): SimpleTableColumnConfig[] {
        return [
            {
                field: "equipment",
                label: this.equipment,
                width: "190px",
                getCustomText: (row) => this.showTextOrPlaceHolder(row.deviceName)
            },
            {
                field: "osc",
                label: this.osc,
                optionalColumn: this.getTextTipIconOptionalColumn(),
                getCustomText: (row) => this.getPortIdentifier(row)
            },
            {
                field: "temperature",
                label: this.temperature,
                getCustomText: (row) => this.showTextOrPlaceHolder(row?.transceiverDiagnosticData?.temperature, CELSIUS)
            },
            {
                field: "voltage",
                label: this.voltage,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.transceiverDiagnosticData?.voltage, VOLT)
            },
            {
                field: "current",
                label: this.current,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.transceiverDiagnosticData?.current, MILI_AMP)
            },
            {
                field: "txPower",
                label: this.txPower,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.transceiverDiagnosticData?.txPower, DECIBEL_MILIWAT)
            },
            {
                field: "rxPower",
                label: this.rxPower,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.transceiverDiagnosticData?.rxPower, DECIBEL_MILIWAT)
            },
            { field: "linkStatus", label: this.linkStatus, getCustomText: (row) => this.getLinkStatusText(row.linkStatus) },
            { field: "alarms", label: this.alarms, optionalColumn: this.getAlarmOptionalColumn("alarmsDiagnosticData") }
        ];
    }

    buildOscColumnsSortable(): Record<string, boolean> {
        const columns = ["equipment", "osc", "temperature", "voltage", "current", "txPower", "rxPower", "linkStatus", "alarms"];

        return this.buildAllColumnsSortable(columns);
    }

    buildLinkColumns(): SimpleTableColumnConfig[] {
        return [
            { field: "name", label: this.name, width: "700px", textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "aEnd", label: this.aEnd, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "zEnd", label: this.zEnd, textToShowWhenEmpty: this.textToShowWhenEmpty },
            {
                field: "distance",
                label: this.distance,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.distance, KILOMETER)
            },
            {
                field: "attenuation",
                label: this.attenuation,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.attenuation, DECIBEL_BY_KILOMETER)
            },
            {
                field: "totalAttenuation",
                label: this.totalAttenuation,
                getCustomText: (row) => this.showTextOrPlaceHolder(row.totalAttenuation, DECIBEL)
            },
            { field: "situation", label: this.situation, getCustomText: (row) => this.getLinkSituation(row) },
            { field: "status", label: this.linkStatus, optionalColumn: this.getLinkStatusOptionalColumn() }
        ];
    }

    buildLinkColumnsSortable(): Record<string, boolean> {
        const columns = ["name", "aEnd", "zEnd", "distance", "attenuation", "totalAttenuation", "situation", "status"];

        return this.buildAllColumnsSortable(columns);
    }

    buildAlarmColumns(): SimpleTableColumnConfig[] {
        return [
            { field: "deviceName", label: this.equipment, customColor: PRIMARY_HOVERED_COLOR },
            { field: "sourceType", label: this.sourceType, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "sourceValue", label: this.sourceValue, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "name", label: this.name, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "information", label: this.info, textToShowWhenEmpty: this.textToShowWhenEmpty },
            { field: "time", label: this.time, textToShowWhenEmpty: this.textToShowWhenEmpty },
            {
                field: "unstable",
                label: this.unstable,
                textToShowWhenEmpty: this.textToShowWhenEmpty,
                getCustomText: (row) => (row.unstable ? "True" : "False")
            },
            { field: "severity", label: this.severity, optionalColumn: this.getStatusOptionColumn() }
        ];
    }

    buildAlarmColumnsSortable(): Record<string, boolean> {
        const columns = ["sourceType", "sourceValue", "name", "info", "time", "unstable", "severity"];

        return this.buildAllColumnsSortable(columns);
    }

    private buildAllColumnsSortable(columns: string[]): Record<string, boolean> {
        const result: Record<string, boolean> = {};
        for (const column of columns) {
            result[column] = true;
        }
        return result;
    }

    private getTextTipIconOptionalColumn(): OptionalColumn {
        return {
            type: ColumnConfigType.TEXT_TIPICON,
            hasTooltip: true,
            tooltipContent: (row) => {
                return this.getPortIdentifier(row);
            }
        };
    }

    private getTextTipIconAmplifiersOptionalColumn(): OptionalColumn {
        return {
            type: ColumnConfigType.TEXT_TIPICON,
            hasTooltip: true,
            tooltipContent: (row) => {
                return this.getPortFormatedIdentifier(row.portIdentifier);
            }
        };
    }

    private getStatusOptionColumn(): OptionalColumn {
        return {
            type: ColumnConfigType.STATUS,
            variant: "circle",
            convert: (status: string) => {
                const { translationKey, bgColor } = CIRCUIT_STATUS[CircuitStatus[status.trim().toUpperCase()]];
                const color = this.labelColorService.getBestColorContrastForForeground(bgColor);
                const label = this.translate.instant(translationKey);

                return { label, bgColor, color };
            }
        };
    }

    private getLinkStatusOptionalColumn(): OptionalColumn {
        return {
            type: ColumnConfigType.STATUS,
            variant: "circle",
            convert: (status: string) => {
                if (status && status.trim().length > 0) {
                    const { translationKey, bgColor } = STATUS[status.trim().toUpperCase()];
                    const color = this.labelColorService.getBestColorContrastForForeground(bgColor);
                    const label = this.translate.instant(translationKey);
                    return { label, bgColor, color };
                }

                return { label: this.textToShowWhenEmpty, bgColor: TRANSPARENT_COLOR, color: WHITE_COLOR };
            }
        };
    }

    private getLinkStatusText(status): string {
        if (status && status.trim().length > 0) {
            const translationKey = LINKS_STATUS_TRANSLATION_MAP[LinkStatus[status.trim().toUpperCase()]];
            return this.translate.instant(translationKey);
        }
        return this.textToShowWhenEmpty;
    }

    private getAlarmOptionalColumn(alarmField: string): OptionalColumn {
        return {
            type: ColumnConfigType.DETAILED_STATUS,
            getStatusInfo: (row) => {
                const firstAlarm = row[alarmField][0];
                return {
                    color: CIRCUIT_STATUS[CircuitStatus[firstAlarm.severity.trim().toUpperCase()]].bgColor,
                    count: row[alarmField].length,
                    label: firstAlarm.name
                };
            },
            onAction: (row: AmplifierDiagnosticData) => this.openAlarmDetailsModal(row.alarms)
        };
    }

    private getLinkSituation(row): string {
        return this.translate.instant(LINK_SITUATION[row.situation.trim().toUpperCase()].translationKey);
    }

    private getPortIdentifier(row: DwdmPortDiagnosticData): string {
        if (row.circuitPortIdentifierData) {
            return this.getPortFormatedIdentifier(row.circuitPortIdentifierData);
        }

        return this.textToShowWhenEmpty;
    }

    private getPortFormatedIdentifier(port: CircuitPortIdentifierData): string {
        return `${port.portModelIdentifier} ${port.chassisIdentifier}/${port.slotIdentifier}/${port.portNo}`;
    }

    private showTextOrPlaceHolder(textSource: string | number, suffix?: string): string {
        return textSource ? `${textSource} ${suffix || ""}` : this.textToShowWhenEmpty;
    }

    private async openAlarmDetailsModal(alarmDetailsData: AlarmDiagnosticData[]) {
        this.modalFactory.openSimpleModal(AlarmDetailsComponent, { alarmDiagnosticData: alarmDetailsData });
    }
}
